import { createContext, use, useState, useCallback, useRef, useEffect } from 'react';
import { announceConfirmHydraTransition, subscribeToHydraTransitionAttempt } from '@customerio/hydra-nav-state';
import {
  Modal,
  ModalBody,
  ModalFooter,
  Paragraph,
  Button,
  ButtonGroup,
  Stack,
  InlineStack,
} from '@customerio/pluma-components/react';
import { isEmptyString } from '@/utils/string';

export type NavigationGuardOptions = {
  /**
   * Whether there are unsaved changes to protect.
   *
   * Accepts either a boolean (re-evaluated on each render) or a getter
   * function that the guard calls **synchronously** when a transition
   * is attempted. The function form lets callers read fresh state
   * (e.g. "just successfully saved, now navigating away") without
   * waiting for a re-render — useful when the navigation that should
   * skip the guard is queued in the same tick that flips the dirty
   * flag.
   */
  hasUnsavedChanges: boolean | (() => boolean);

  /**
   * Custom message to show in the confirmation modal
   */
  message?: string;

  /**
   * Callback to save changes before navigating away
   * If provided, a "Save and Continue" option will be shown
   */
  onSave?: () => Promise<void> | void;

  /**
   * Callback to handle errors from the save operation
   */
  onError?: (error: unknown) => void;

  /**
   * Callback called when user chooses to discard changes
   */
  onDiscard?: () => void;
};

/** Normalize the boolean-or-getter form to a boolean. */
function resolveHasUnsavedChanges(value: NavigationGuardOptions['hasUnsavedChanges']): boolean {
  return typeof value === 'function' ? value() : value;
}

type NavigationGuardState =
  | (NavigationGuardOptions & {
      /**
       * Whether the navigation guard modal is currently open
       */
      isOpen: true;

      /**
       * Function to proceed with navigation after confirmation
       */
      proceedWithNavigation: () => void;
    })
  | (Partial<NavigationGuardOptions> & {
      /**
       * Whether the navigation guard modal is currently open
       */
      isOpen: false;
    });

const NavigationGuardContext = createContext<{
  registerGuard: (options: NavigationGuardOptions) => void;
  unregisterGuard: () => void;
} | null>(null);

export function useNavigationGuard(options: NavigationGuardOptions) {
  const context = use(NavigationGuardContext);

  if (context == null) {
    throw new Error('useNavigationGuard must be used within a NavigationGuardProvider');
  }

  const { hasUnsavedChanges, message, onSave, onError, onDiscard } = options;
  // For the beforeunload + effect deps we need a concrete boolean.
  // Function callers get re-evaluated as their state changes naturally
  // (the component re-renders → this hook runs → resolved value flips).
  const resolvedHasUnsavedChanges = resolveHasUnsavedChanges(hasUnsavedChanges);

  // Browser-level protection for refresh/close.
  useEffect(() => {
    if (!resolvedHasUnsavedChanges) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };

    window.addEventListener('beforeunload', handleBeforeUnload, { capture: true });
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload, { capture: true });
    };
  }, [resolvedHasUnsavedChanges]);

  // Mirror the latest `hasUnsavedChanges` into a ref on every render so
  // the provider can read fresh state synchronously inside the hydra
  // transition listener — without waiting for `useEffect` to re-run.
  // This is what enables the function form to skip the modal on a
  // post-save navigation queued in the same tick.
  const hasUnsavedChangesRef = useRef(hasUnsavedChanges);
  hasUnsavedChangesRef.current = hasUnsavedChanges;

  useEffect(() => {
    context.registerGuard({
      message,
      onSave,
      onError,
      onDiscard,
      // Always register as a getter so the provider has one shape to
      // call. Reads the latest value from the ref so post-render state
      // flips (e.g. a save just completed) are visible immediately.
      hasUnsavedChanges: () => resolveHasUnsavedChanges(hasUnsavedChangesRef.current),
    });

    return () => {
      context.unregisterGuard();
    };
  }, [context, message, onSave, onError, onDiscard]);
}

type NavigationGuardProviderProps = { children?: React.ReactNode };

export function NavigationGuardProvider({ children }: NavigationGuardProviderProps) {
  const [state, setState] = useState<NavigationGuardState>({
    isOpen: false,
  });

  const [isSaving, setIsSaving] = useState(false);
  const currentGuardRef = useRef<NavigationGuardOptions | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  // Set up the subscription once when the provider mounts
  useEffect(() => {
    unsubscribeRef.current = subscribeToHydraTransitionAttempt(() => {
      const currentGuard = currentGuardRef.current;

      // If no guard is registered or no unsaved changes, immediately confirm
      if (currentGuard == null || !resolveHasUnsavedChanges(currentGuard.hasUnsavedChanges)) {
        announceConfirmHydraTransition();
        return;
      }

      // If modal is already open, don't show another one
      if (state.isOpen) {
        return;
      }

      // Show the modal for unsaved changes
      setState({
        isOpen: true,
        proceedWithNavigation: () => announceConfirmHydraTransition(),
        ...currentGuard,
      });
    });

    return () => {
      if (unsubscribeRef.current != null) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [state.isOpen]);

  const registerGuard = useCallback((options: NavigationGuardOptions) => {
    currentGuardRef.current = options;
  }, []);

  const unregisterGuard = useCallback(() => {
    currentGuardRef.current = null;
  }, []);

  // Flip `isOpen` to false without dropping the rest of the state — the
  // modal stays mounted during its close animation, so wiping `message`
  // / `onSave` here would briefly re-render with the fallback copy
  // before unmount. `clearState` (the `onCloseComplete` handler) is the
  // one that actually drops the options once the animation finishes.
  //
  // The `...rest` spread carries `message`, `onSave`, `onError`,
  // `onDiscard`, and `hasUnsavedChanges` into the closed-state shape.
  // That's safe because `NavigationGuardState`'s `isOpen: false` branch
  // is typed as `Partial<NavigationGuardOptions>`, which accepts any
  // subset of those fields — but no consumer should read them while
  // `isOpen` is false (the open branch is what carries the guarantee).
  const closeKeepingOptions = useCallback(() => {
    setState((previous) => {
      if (!previous.isOpen) return previous;
      const { proceedWithNavigation: _proceed, isOpen: _open, ...rest } = previous;
      return { ...rest, isOpen: false };
    });
  }, []);

  const handleSaveAndContinue = useCallback(async () => {
    if (!state.isOpen || state.onSave == null) {
      return;
    }

    try {
      setIsSaving(true);
      await state.onSave();
      state.proceedWithNavigation();
      closeKeepingOptions();
    } catch (error) {
      // Close the modal on save failure so the underlying page —
      // which typically owns the error UI (inline banner, form-field
      // errors) — becomes visible again. Without this, the modal
      // sits over the page hiding whatever feedback `onSave` already
      // rendered, and the user is left with only a stopped spinner.
      closeKeepingOptions();
      state.onError?.(error);
    } finally {
      setIsSaving(false);
    }
  }, [state, closeKeepingOptions]);

  const handleDiscardAndContinue = useCallback(() => {
    if (!state.isOpen) {
      return;
    }

    state.onDiscard?.();
    state.proceedWithNavigation();
    closeKeepingOptions();
  }, [state, closeKeepingOptions]);

  const handleCancel = useCallback(() => {
    closeKeepingOptions();
    setIsSaving(false);
  }, [closeKeepingOptions]);

  // Clear state when modal is closed to prevent flash
  function clearState() {
    setState({
      isOpen: false,
    });
  }

  return (
    <NavigationGuardContext.Provider value={{ registerGuard, unregisterGuard }}>
      <Modal isOpen={state.isOpen} onClose={handleCancel} onCloseComplete={clearState} title="Just a minute!">
        <ModalBody>
          {state.message != null && !isEmptyString(state.message) ? (
            <Paragraph>{state.message}</Paragraph>
          ) : (
            <Stack gap="200">
              <Paragraph>You have unsaved changes.</Paragraph>
              <Paragraph>Please save your changes or discard them to continue.</Paragraph>
            </Stack>
          )}
        </ModalBody>
        <ModalFooter as={InlineStack} alignBlock="center" alignInline="space-between" gap="200">
          <Button variant="secondary" size="sm" onClick={handleCancel} disabled={isSaving}>
            Go back
          </Button>
          <ButtonGroup groupVariant="spaced">
            <Button variant="secondary" size="sm" isDanger onClick={handleDiscardAndContinue} disabled={isSaving}>
              Discard changes
            </Button>
            {state.onSave != null ? (
              <Button
                variant="primary"
                size="sm"
                onClick={handleSaveAndContinue}
                isLoading={isSaving}
                disabled={isSaving}
              >
                Save & Continue
              </Button>
            ) : null}
          </ButtonGroup>
        </ModalFooter>
      </Modal>
      {children}
    </NavigationGuardContext.Provider>
  );
}
